import { Task } from '@lit/task';
import { SlDialog } from '@shoelace-style/shoelace';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { Artifact } from '../artifact.js';
import { linkStyles } from './cssstyles.js';

export function isHTMLAttachment(artifact: Artifact) {
  const contentType = artifact.contentType.split(';')[0].trim().toLowerCase();
  return contentType === 'text/html';
}

type View = 'rendered' | 'source';

@customElement('html-attachment')
class HTMLAttachment extends LitElement {
  @property({ attribute: false }) artifact?: Artifact;
  @state() private _opened: boolean = false;
  @state() private _view: View = 'rendered';

  @query('sl-dialog') private _dialog?: SlDialog;

  private _contentTask = new Task(this, {
    args: () => [this.artifact, this._opened] as const,
    task: async ([artifact, opened], { signal }) => {
      if (!artifact || !opened)
        return undefined;
      const response = await fetch(artifact.url, { signal });
      if (!response.ok)
        throw new Error(`Failed to load: ${response.status} ${response.statusText}`);
      return await response.text();
    },
  });

  private _open() {
    this._opened = true;
    this._dialog?.show();
  }

  override render() {
    const artifact = this.artifact;
    if (!artifact)
      return nothing;
    return html`
      <h-box class=row>
        <h-box class=clickable @click=${this._open}>
          <sl-icon name=file-earmark-zip></sl-icon>
          <span class=filename>${artifact.fileName}</span>
        </h-box>
        <a class=download href=${artifact.url} download=${artifact.fileName}>
          <sl-icon name=download></sl-icon>
        </a>
      </h-box>
      <sl-dialog>
        <h-box class=dialog-toolbar slot=label>
          <span class=dialog-title>${artifact.fileName}</span>
          <x-filler></x-filler>
          <sl-radio-group size=small value=${this._view} @sl-change=${(e: CustomEvent) => { this._view = (e.target as HTMLInputElement).value as View; }}>
            <sl-radio-button value=rendered>Rendered</sl-radio-button>
            <sl-radio-button value=source>Source</sl-radio-button>
          </sl-radio-group>
        </h-box>
        ${this._contentTask.render({
          initial: () => nothing,
          pending: () => nothing,
          error: (e) => html`<div class=error>${(e as Error).message}</div>`,
          complete: (text) => {
            if (text === undefined)
              return nothing;
            if (this._view === 'rendered')
              return html`<iframe class=full sandbox="allow-scripts" .srcdoc=${text}></iframe>`;
            return html`<ansi-text class=source .text=${text}></ansi-text>`;
          },
        })}
      </sl-dialog>
    `;
  }

  static styles = [linkStyles, css`
    :host {
      display: block;
    }

    .row {
      gap: var(--sl-spacing-x-small);
      align-items: center;
      font-size: var(--sl-font-size-small);
      padding-left: calc(1em + var(--sl-spacing-2x-small));
    }

    .clickable {
      gap: var(--sl-spacing-2x-small);
      align-items: center;
      cursor: pointer;
      user-select: none;
    }

    .filename {
      font-family: var(--sl-font-mono);
    }

    .download {
      display: inline-flex;
      align-items: center;
      color: var(--sl-color-neutral-600);
    }

    .download:hover {
      color: var(--sl-color-primary-600);
    }

    sl-dialog {
      --width: 90vw;
    }

    .dialog-toolbar {
      align-items: center;
      gap: var(--sl-spacing-small);
      width: 100%;
    }

    .dialog-title {
      font-family: var(--sl-font-mono);
    }

    .full {
      display: block;
      width: 100%;
      height: 80vh;
      border: none;
    }

    .source {
      display: block;
      height: 80vh;
      overflow: auto;
      background-color: var(--sl-color-neutral-50);
      border-radius: var(--sl-border-radius-medium);
      padding: var(--sl-spacing-medium);
    }

    .error {
      color: var(--sl-color-danger-600);
      font-size: var(--sl-font-size-small);
    }
  `];
}
